import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
    AlertTriangle,
    ArrowLeft,
    ArrowRight,
    Check,
    CheckCircle2,
    Download,
    File as FileIcon,
    RotateCcw,
    Upload,
    X,
    XCircle,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../pageScaffold/PageScaffold';
import BooleanPill from '../common/BooleanPill';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { useAppBarTitle } from '../layout/AppBarActions';
import { KitButton, KitCard, SecH } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitDropzone, KitFormBar, KitKV, KitProgress, KitSeg, KitWizard } from '../common/MobileKitExtras';

const MUploadIcon = protoIcon('upload');
const MDownloadIcon = protoIcon('download');
const MInfoIcon = protoIcon('info');
const MFileIcon = protoIcon('file');

/**
 * Wizard genérico de importação em massa via CSV — DESIGN-SYSTEM.md §5.5
 * (`step-pills`), estendido por um novo padrão "wizard-stepper" (3 passos
 * conectados, um painel por vez) introduzido por
 * `prototipo-importar-produtos.html`. Ver nota de extensão em
 * DESIGN-SYSTEM.md.
 *
 * Parametrizado por entidade para ser reaproveitado por outras telas de
 * importação sem duplicar o wizard inteiro — hoje usado por
 * `pages/products/Import.js` e `pages/services/Import.js`: cada tela
 * consumidora só precisa informar o contrato de import
 * (endpoint/colunas/exemplo/breadcrumb).
 *
 * Todas as props de negócio (endpoint, colunas do modelo, arquivo de
 * exemplo) são responsabilidade de quem instancia — este componente não
 * conhece nada de "produto" nem de nenhuma outra entidade específica.
 */
export default function ImportWizard({
    pageIcon: PageIcon = Upload,
    pageTitle,
    pageDescription,
    backTo,
    backLabel,
    templateHref,
    templateFileName,
    templateColumns,
    separators = [
        { value: ';', label: 'Ponto e vírgula' },
        { value: ',', label: 'Vírgula' },
    ],
    previewHeaders,
    previewRows,
    importEndpoint,
    entityLabelPlural = 'itens',
    acceptExtension = '.csv',
    maxSizeMB = 10,
    mobileTip,
}) {
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell ? pageTitle : '');
    const fileInputRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(1);
    const [maxStepReached, setMaxStepReached] = useState(1);
    const [separator, setSeparator] = useState(separators[0]?.value || ';');
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [result, setResult] = useState(null); // { errors, totalLines, importedCount, elapsedSeconds, fileName }
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const separatorLabel = separators.find((s) => s.value === separator)?.label || separator;

    const goToStep = (n) => {
        if (n > maxStepReached + 1) return;
        setCurrentStep(n);
        setMaxStepReached((m) => Math.max(m, n));
    };

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = templateHref;
        link.download = templateFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSnackbar('Modelo CSV baixado com sucesso.');
    };

    const validateAndSetFile = (candidate) => {
        if (!candidate) return;
        const extensionOk = candidate.name?.toLowerCase().endsWith(acceptExtension);
        if (!extensionOk) {
            showSnackbar(`Selecione um arquivo ${acceptExtension.toUpperCase()}.`, 'error');
            return;
        }
        if (candidate.size > maxSizeMB * 1024 * 1024) {
            showSnackbar(`O arquivo excede o limite de ${maxSizeMB}MB.`, 'error');
            return;
        }
        setFile(candidate);
        setResult(null);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const removeFile = () => {
        setFile(null);
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const fmtSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Conta as linhas de dados do próprio arquivo (excluindo o cabeçalho)
    // inteiramente no cliente, só para compor o card "linhas no arquivo" do
    // resumo — não deriva nenhuma regra de negócio, é só contagem de texto.
    const countDataLines = async (f) => {
        try {
            const text = await f.text();
            const lines = text.split(/\r\n|\r|\n/).filter((l) => l.trim() !== '');
            return Math.max(lines.length - 1, 0);
        } catch {
            return null;
        }
    };

    const startImport = async () => {
        if (!file || uploading) return;
        setUploading(true);
        setUploadProgress(0);
        setResult(null);

        const totalLinesPromise = countDataLines(file);

        const timer = setInterval(() => {
            setUploadProgress((p) => (p >= 90 ? p : p + Math.floor(Math.random() * 10) + 6));
        }, 220);

        const startedAt = Date.now();
        const formData = new FormData();
        formData.append('file', file);

        try {
            // `encodeURIComponent` é obrigatório aqui: um `;` cru na query
            // string faz o `net/url` do Go (fv-api) rejeitar a requisição
            // inteira com "invalid semicolon separator in query" (Go trata
            // `;` como separador de pares chave=valor desde a correção de
            // segurança do pacote `net/url`) — bug pré-existente (já
            // presente na implementação anterior deste wizard, que também
            // interpolava `separator` cru na URL) que travava silenciosamente
            // a opção padrão "Ponto e vírgula". Corrigido só no client
            // (nenhuma mudança de contrato: o servidor recebe o mesmo `;`
            // depois do percent-decode).
            const response = await httpClient.post(importEndpoint(encodeURIComponent(separator)), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            clearInterval(timer);
            setUploadProgress(100);

            const totalLines = await totalLinesPromise;
            const elapsedSeconds = ((Date.now() - startedAt) / 1000).toFixed(1);

            setTimeout(() => {
                if (response.status === 201) {
                    setResult({
                        errors: [],
                        totalLines,
                        importedCount: totalLines,
                        elapsedSeconds,
                        fileName: file.name,
                    });
                    showSnackbar(`${totalLines ?? ''} ${entityLabelPlural} importados com sucesso.`.trim());
                } else {
                    const errors = Array.isArray(response.data) ? response.data : [];
                    setResult({
                        errors,
                        totalLines,
                        importedCount: 0,
                        elapsedSeconds,
                        fileName: file.name,
                    });
                    showSnackbar(`Importação não concluída — ${errors.length} erro(s) encontrado(s).`, 'error');
                }
                setUploading(false);
            }, 350);
        } catch (error) {
            clearInterval(timer);
            setUploading(false);
            setUploadProgress(0);
            showSnackbar('Erro na conexão. Tente novamente.', 'error');
        }
    };

    const resetWizard = () => {
        removeFile();
        setUploadProgress(0);
        setMaxStepReached(1);
        setCurrentStep(1);
    };

    const downloadErrorReport = () => {
        if (!result?.errors?.length) return;
        const headers = ['linha', 'coluna', 'motivo_do_erro'];
        const csvRows = [headers.join(';')].concat(
            result.errors.map((e) => [
                e.line === 0 ? 'arquivo inteiro' : e.line,
                e.column,
                `"${String(e.error || '').replace(/"/g, "'")}"`,
            ].join(';'))
        );
        const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'relatorio_erros_importacao.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSnackbar('Relatório de erros baixado.');
    };

    const steps = [
        { n: 1, title: 'Modelo', sub: 'Baixe o CSV de exemplo' },
        { n: 2, title: 'Configurar', sub: 'Separador de colunas' },
        { n: 3, title: 'Upload', sub: 'Enviar e revisar erros' },
    ];

    // ---- Shell mobile (`prototipos-mobile/produtos/importar.html`): 3 passos —
    // 1 Enviar arquivo (+ modelo e colunas), 2 Configurar (separador), 3 Resultado.
    // Mesmos dados/chamadas do fluxo desktop (`startImport`, `goToStep`).
    if (isMobileShell) {
        const mobileLabels = ['Passo 1 de 3 · Enviar arquivo', 'Passo 2 de 3 · Configurar', result ? 'Passo 3 de 3 · Resultado' : 'Passo 3 de 3 · Importando'];
        return (
            <PageScaffold
                links={[]}
                titlePageTarget={pageTitle}
                openSnackbar={snackbar.open}
                setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            >
                <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
                    <KitWizard step={currentStep} label={mobileLabels[currentStep - 1]} />
                    <input ref={fileInputRef} type="file" accept={acceptExtension} hidden onChange={(e) => validateAndSetFile(e.target.files?.[0])} />

                    {currentStep === 1 && (
                        <>
                            <KitDropzone icon={MUploadIcon} iconSize={32} height={160} onClick={() => fileInputRef.current?.click()}>
                                <b>{file ? file.name : 'Toque para escolher o arquivo'}</b>
                                <Box component="span" sx={{ fontSize: 11.5 }}>
                                    {file ? `${fmtSize(file.size)} · toque para trocar` : `${acceptExtension} · até ${maxSizeMB} MB`}
                                </Box>
                            </KitDropzone>
                            <Box sx={{ mt: '12px' }}>
                                <KitButton variant="soft" block onClick={handleDownloadTemplate} startIcon={<MDownloadIcon size={18} />}>
                                    Baixar planilha modelo
                                </KitButton>
                            </Box>
                            <SecH title="Colunas esperadas" />
                            <KitCard>
                                {templateColumns.map((col) => (
                                    <KitKV key={col.key} label={col.key} value={col.required ? 'obrigatório' : 'opcional'} />
                                ))}
                            </KitCard>
                            {mobileTip && (
                                <Box sx={{ mt: '12px' }}>
                                    <KitBanner title="Dica" icon={MInfoIcon} iconSize={18}>{mobileTip}</KitBanner>
                                </Box>
                            )}
                            <KitFormBar>
                                <KitButton variant="primary" disabled={!file} onClick={() => goToStep(2)}>Continuar</KitButton>
                            </KitFormBar>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <SecH title="Separador de colunas" first />
                            <KitSeg options={separators.map((sep) => ({ value: sep.value, label: `${sep.label} ( ${sep.value} )` }))} value={separator} onChange={setSeparator} />
                            <SecH title="Arquivo" />
                            <KitCard>
                                <KitKV label="Nome" value={file?.name || '—'} />
                                <KitKV label="Tamanho" value={file ? fmtSize(file.size) : '—'} />
                                <KitKV label="Separador" value={`${separatorLabel} ( ${separator} )`} />
                            </KitCard>
                            <Box sx={{ mt: '12px' }}>
                                <KitBanner tone="info" icon={MInfoIcon} iconSize={18}>
                                    O arquivo deve conter uma linha de cabeçalho. Os dados são validados linha a linha pelo servidor.
                                </KitBanner>
                            </Box>
                            <KitFormBar>
                                <KitButton onClick={() => goToStep(1)}>Voltar</KitButton>
                                <KitButton
                                    variant="primary"
                                    disabled={!file}
                                    onClick={() => {
                                        goToStep(3);
                                        startImport();
                                    }}
                                >
                                    Iniciar importação
                                </KitButton>
                            </KitFormBar>
                        </>
                    )}

                    {currentStep === 3 && !result && (
                        <KitCard>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px', fontSize: 13 }}>
                                <span>{uploading ? 'Processando arquivo…' : 'Aguardando'}</span>
                                <b>{Math.min(uploadProgress, 100)}%</b>
                            </Box>
                            <KitProgress value={Math.min(uploadProgress, 100)} />
                            {!uploading && (
                                <Box sx={{ mt: '14px' }}>
                                    <KitButton block onClick={() => goToStep(2)}>Voltar</KitButton>
                                </Box>
                            )}
                        </KitCard>
                    )}

                    {currentStep === 3 && result && (
                        <>
                            <KitCard>
                                <KitKV label="Arquivo" value={result.fileName} />
                                <KitKV label="Linhas no arquivo" value={result.totalLines ?? '—'} />
                                <KitKV label={`${entityLabelPlural} importados`} value={result.importedCount} />
                                <KitKV label="Erros" value={result.errors.length} warn={result.errors.length > 0} />
                                <KitKV label="Tempo" value={`${result.elapsedSeconds}s`} />
                            </KitCard>
                            {result.errors.length === 0 ? (
                                <Box sx={{ mt: '12px' }}>
                                    <KitBanner tone="success" title="Nenhum erro encontrado" icon={MFileIcon} iconSize={18}>
                                        Todos os {entityLabelPlural} do arquivo foram importados com sucesso.
                                    </KitBanner>
                                </Box>
                            ) : (
                                <>
                                    <SecH title="Erros encontrados" />
                                    <KitCard sx={{ p: '4px 16px' }}>
                                        {result.errors.map((err, i) => (
                                            <KitKV key={i} label={`${err.line === 0 ? 'Arquivo inteiro' : `Linha ${err.line}`} · ${err.column}`} value={err.error} />
                                        ))}
                                    </KitCard>
                                    <Box sx={{ mt: '8px', fontSize: 11.5, color: 'text.disabled' }}>
                                        Nenhum item foi importado — corrija os erros e reenvie o arquivo completo.
                                    </Box>
                                    <Box sx={{ mt: '12px' }}>
                                        <KitButton variant="soft" block onClick={downloadErrorReport} startIcon={<MDownloadIcon size={18} />}>Baixar relatório de erros</KitButton>
                                    </Box>
                                </>
                            )}
                            <KitFormBar>
                                <KitButton onClick={resetWizard}>Importar outro</KitButton>
                                <KitButton variant="primary" onClick={() => navigate(backTo)}>Concluir</KitButton>
                            </KitFormBar>
                        </>
                    )}
                </Box>
            </PageScaffold>
        );
    }

    const cardSx = {
        borderRadius: (t) => `${t.customRadius.card}px`,
        boxShadow: (t) => t.customShadows.sm,
    };

    const specTableSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
    };

    const thSx = {
        fontSize: '10.5px',
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        color: 'text.disabled',
        fontWeight: 700,
        backgroundColor: 'background.subtle',
        borderBottom: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'nowrap',
    };

    const tdSx = {
        fontSize: '12.5px',
        color: 'text.secondary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
    };

    const monoSx = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px' };

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={pageTitle}
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
            message={snackbar.message}
            severity={snackbar.severity}
        >
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                {/* page-head */}
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', background: (t) => t.palette.gradient.brand,
                        }}>
                            <PageIcon size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                {pageTitle}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                {pageDescription}
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowLeft size={15} strokeWidth={2} />} onClick={() => navigate(backTo)}>
                        {backLabel}
                    </Button>
                </Box>

                {/* wizard-stepper — extensão do padrão step-pills (§5.5), ver
                    DESIGN-SYSTEM.md §5.17 */}
                <Card sx={{ ...cardSx, mb: 2.25, p: { xs: 2, sm: '20px 24px' } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: { xs: 1.75, sm: 0 } }}>
                        {steps.map((step, idx) => {
                            const isActive = step.n === currentStep;
                            const isDone = step.n < currentStep;
                            const clickable = step.n <= maxStepReached + 1;
                            return (
                                <Box key={step.n} sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}>
                                    <Box
                                        onClick={() => clickable && goToStep(step.n)}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: clickable ? 'pointer' : 'default', flex: 1 }}
                                    >
                                        <Box sx={{
                                            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 13, fontWeight: 800,
                                            border: '2px solid',
                                            borderColor: isDone ? 'success.main' : (isActive ? 'primary.main' : 'divider'),
                                            backgroundColor: isDone ? 'success.main' : (isActive ? 'info.bg' : 'background.subtle'),
                                            color: isDone ? '#fff' : (isActive ? 'primary.main' : 'text.disabled'),
                                            transition: 'all .15s ease',
                                        }}>
                                            {isDone ? <Check size={15} strokeWidth={3} /> : step.n}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: isActive || isDone ? 'text.primary' : 'text.disabled', lineHeight: 1.3 }}>
                                                {step.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.3 }}>
                                                {step.sub}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {idx < steps.length - 1 && (
                                        <Box sx={{
                                            display: { xs: 'none', sm: 'block' },
                                            flex: '0 0 auto', width: 56, height: 2, mx: '14px', mt: '-18px',
                                            backgroundColor: step.n < currentStep ? 'success.main' : 'divider',
                                            transition: 'background-color .2s ease',
                                        }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Card>

                {/* ============ STEP 1: MODELO ============ */}
                {currentStep === 1 && (
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                            <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>
                                Formato esperado do arquivo
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2.25 }}>
                                O arquivo deve ser um CSV com a primeira linha contendo os cabeçalhos exatamente como no modelo abaixo. Baixe o modelo para preencher sem risco de erro de coluna.
                            </Typography>

                            <TableContainer sx={specTableSx}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={thSx}>Coluna</TableCell>
                                            <TableCell sx={thSx}>Obrigatório</TableCell>
                                            <TableCell sx={thSx}>Tipo</TableCell>
                                            <TableCell sx={thSx}>Exemplo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {templateColumns.map((col) => (
                                            <TableRow key={col.key} sx={{ '&:hover': { backgroundColor: 'background.subtle' }, '&:last-child td': { borderBottom: 'none' } }}>
                                                <TableCell sx={{ ...tdSx, ...monoSx, fontWeight: 700, color: 'text.primary' }}>{col.key}</TableCell>
                                                <TableCell sx={tdSx}><BooleanPill value={col.required} /></TableCell>
                                                <TableCell sx={tdSx}>{col.type}</TableCell>
                                                <TableCell sx={{ ...tdSx, ...monoSx }}>{col.example}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Alert severity="warning" icon={<AlertTriangle size={16} strokeWidth={2} />} sx={{ mt: 2 }}>
                                Não altere os nomes das colunas do modelo — linhas com colunas desconhecidas ou faltando serão rejeitadas no passo 3.
                            </Alert>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2.75, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Passo 1 de 3</Typography>
                                <Stack direction="row" spacing={1.25}>
                                    <Button variant="contained" startIcon={<Download size={15} strokeWidth={2} />} onClick={handleDownloadTemplate}>
                                        Baixar modelo CSV
                                    </Button>
                                    <Button variant="outlined" endIcon={<ArrowRight size={15} strokeWidth={2} />} onClick={() => goToStep(2)}>
                                        Avançar
                                    </Button>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* ============ STEP 2: CONFIGURAR ============ */}
                {currentStep === 2 && (
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                            <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>
                                Configurar leitura do arquivo
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2.25 }}>
                                Escolha o separador de colunas usado no seu CSV. A prévia abaixo é recalculada com o separador escolhido, usando linhas de exemplo.
                            </Typography>

                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px', display: 'block' }}>
                                Separador de colunas
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                value={separator}
                                onChange={(e, value) => value && setSeparator(value)}
                                sx={{ width: 'fit-content' }}
                            >
                                {separators.map((sep) => (
                                    <ToggleButton key={sep.value} value={sep.value} sx={{ gap: 1, px: 2 }}>
                                        {sep.label}
                                        <Box component="span" sx={{
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            fontSize: 11,
                                            px: '5px',
                                            borderRadius: '5px',
                                            backgroundColor: separator === sep.value ? 'info.bg' : 'background.subtle',
                                            border: '1px solid',
                                            borderColor: separator === sep.value ? 'info.bg' : 'divider',
                                            color: separator === sep.value ? 'primary.main' : 'text.disabled',
                                        }}>
                                            {sep.value}
                                        </Box>
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            <FormControlLabel
                                sx={{ mt: 2, ml: 0 }}
                                control={<Checkbox checked disabled size="small" />}
                                label={
                                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                                        O arquivo contém uma linha de cabeçalho (obrigatório)
                                    </Typography>
                                }
                            />

                            <Box sx={{ mt: 2.75 }}>
                                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px', display: 'block' }}>
                                    Prévia com o separador selecionado
                                </Typography>
                                <TableContainer sx={specTableSx}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {previewHeaders.map((h) => (
                                                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {previewRows.map((row, i) => (
                                                <TableRow key={i} sx={{ '&:hover': { backgroundColor: 'background.subtle' }, '&:last-child td': { borderBottom: 'none' } }}>
                                                    {row.map((cell, j) => (
                                                        <TableCell key={j} sx={{ ...tdSx, ...monoSx, ...(j === 0 ? { fontWeight: 700, color: 'text.primary' } : {}) }}>
                                                            {cell}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1 }}>
                                    Prévia gerada a partir de um arquivo de exemplo — no upload real, o próprio arquivo enviado é validado linha a linha pelo servidor.
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2.75, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Passo 2 de 3</Typography>
                                <Stack direction="row" spacing={1.25}>
                                    <Button variant="outlined" startIcon={<ArrowLeft size={15} strokeWidth={2} />} onClick={() => goToStep(1)}>
                                        Voltar
                                    </Button>
                                    <Button variant="contained" endIcon={<ArrowRight size={15} strokeWidth={2} />} onClick={() => goToStep(3)}>
                                        Avançar
                                    </Button>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* ============ STEP 3: UPLOAD ============ */}
                {currentStep === 3 && !result && (
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                            <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>
                                Enviar arquivo
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2.25 }}>
                                Separador selecionado: <Box component="b" sx={{ color: 'text.primary' }}>{separatorLabel} ( {separator} )</Box>
                                {' — '}
                                <Box component="button" type="button" onClick={() => goToStep(2)} sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: 'primary.main', fontWeight: 600, fontSize: 'inherit' }}>
                                    alterar
                                </Box>
                            </Typography>

                            {!file ? (
                                <Box
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: dragActive ? 'primary.main' : 'divider',
                                        borderRadius: '16px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        backgroundColor: dragActive ? 'info.bg' : 'background.subtle',
                                        p: '40px 20px',
                                        transition: 'all .15s ease',
                                        '&:hover': { borderColor: 'primary.main' },
                                    }}
                                >
                                    <Box sx={{
                                        width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'primary.main',
                                    }}>
                                        <Upload size={24} strokeWidth={2} />
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: '4px' }}>
                                        Arraste o arquivo CSV aqui
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                        ou clique para selecionar do seu computador ({acceptExtension}, até {maxSizeMB}MB)
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={acceptExtension}
                                        hidden
                                        onChange={(e) => validateAndSetFile(e.target.files?.[0])}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, p: '12px 14px',
                                    border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle',
                                }}>
                                    <Box sx={{ width: 36, height: 36, borderRadius: '9px', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <FileIcon size={18} strokeWidth={2} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{file.name}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{fmtSize(file.size)}</Typography>
                                    </Box>
                                    {!uploading && (
                                        <Tooltip title="Remover arquivo">
                                            <IconButton size="small" onClick={removeFile} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                                                <X size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}

                            {uploading && (
                                <Box sx={{ mt: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '6px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={14} thickness={5} />
                                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Processando arquivo…</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{Math.min(uploadProgress, 100)}%</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(uploadProgress, 100)}
                                        sx={{
                                            height: 10, borderRadius: '6px', backgroundColor: 'background.subtle',
                                            '& .MuiLinearProgress-bar': { borderRadius: '6px', backgroundImage: (t) => t.palette.gradient.brand },
                                        }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2.75, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Passo 3 de 3</Typography>
                                <Stack direction="row" spacing={1.25}>
                                    <Button variant="outlined" disabled={uploading} startIcon={<ArrowLeft size={15} strokeWidth={2} />} onClick={() => goToStep(2)}>
                                        Voltar
                                    </Button>
                                    <Button variant="contained" disabled={!file || uploading} startIcon={<Upload size={15} strokeWidth={2} />} onClick={startImport}>
                                        {uploading ? 'Importando...' : 'Iniciar importação'}
                                    </Button>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* ============ RESULTADO ============ */}
                {currentStep === 3 && result && (
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                            <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>
                                Resultado da importação
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2.25 }}>
                                Processamento concluído em {result.elapsedSeconds}s a partir de "{result.fileName}".
                            </Typography>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: '14px', mb: 2.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle' }}>
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <FileIcon size={18} strokeWidth={2} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{result.totalLines ?? '—'}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>linhas no arquivo</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle' }}>
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', backgroundColor: 'success.bg', color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <CheckCircle2 size={18} strokeWidth={2} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{result.importedCount}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{entityLabelPlural} importados</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle' }}>
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', backgroundColor: 'error.bg', color: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <XCircle size={18} strokeWidth={2} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{result.errors.length}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>erro(s) encontrado(s)</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {result.errors.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 4, color: 'text.disabled' }}>
                                    <Box sx={{ width: 60, height: 60, borderRadius: '16px', backgroundColor: 'success.bg', color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.75 }}>
                                        <CheckCircle2 size={28} strokeWidth={2} />
                                    </Box>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary', mb: '4px' }}>
                                        Nenhum erro encontrado
                                    </Typography>
                                    <Typography sx={{ fontSize: 12.5, maxWidth: 340 }}>
                                        Todos os {entityLabelPlural} do arquivo foram importados com sucesso.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px', display: 'block' }}>
                                        Erros encontrados
                                    </Typography>
                                    <TableContainer sx={specTableSx}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={thSx}>Linha</TableCell>
                                                    <TableCell sx={thSx}>Coluna</TableCell>
                                                    <TableCell sx={thSx}>Motivo do erro</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.errors.map((err, i) => (
                                                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: 'background.subtle' }, '&:last-child td': { borderBottom: 'none' } }}>
                                                        <TableCell sx={{ ...tdSx, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                                                            {err.line === 0 ? 'Arquivo inteiro' : `Linha ${err.line}`}
                                                        </TableCell>
                                                        <TableCell sx={{ ...tdSx, ...monoSx }}>{err.column}</TableCell>
                                                        <TableCell sx={tdSx}>{err.error}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1 }}>
                                        Nenhum {entityLabelPlural.replace(/s$/, '')} foi importado — corrija os erros acima e reenvie o arquivo completo.
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2.75, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                    {result.errors.length > 0 ? `${result.errors.length} erro(s) para corrigir` : 'Importação concluída.'}
                                </Typography>
                                <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                                    {result.errors.length > 0 && (
                                        <Button variant="outlined" startIcon={<Download size={15} strokeWidth={2} />} onClick={downloadErrorReport}>
                                            Baixar relatório de erros
                                        </Button>
                                    )}
                                    <Button variant="outlined" startIcon={<RotateCcw size={15} strokeWidth={2} />} onClick={resetWizard}>
                                        Importar outro arquivo
                                    </Button>
                                    <Button variant="contained" onClick={() => navigate(backTo)}>
                                        Concluir
                                    </Button>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </PageScaffold>
    );
}
